import { useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { getTranslations } from '@assets/i18n/config';
import { CONFIG } from '@config';
import { WorkflowObjectTypes } from '@generated/graphql';
import { getLanguageDefaults } from '@onevesthq/ov-translations';
import { householdOwnsPortfolio } from '@ovComponents/4-module/householdPortfolio/householdOwnsPortfolio';
import { WorkflowContext } from '@ovComponents/4-module/workflowCompletion/workflowCompletionContext';
import { PageObjectType } from '@pages/setting/PageConfigurationDetail/pageConfigurationTypes';
import { UserContext } from '@providers/userContextProvider';
import { FlagsmithFlags } from '@utils/flagsmith';
import { useToast } from 'hephaestus/web';
import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useFeatureFlag } from '../../../../../hooks/useFeatureFlag';
import { AssignPortfolioToSubAccountVisual } from './assignPortfolioToSubAccount.visual';
import {
  applySuggestedPortfolio,
  buildAssignPortfolioFetchThemesInput,
  determineUserSuitability,
  FETCH_GOAL,
  FETCH_GOAL_WITH_SUBACCOUNTS,
  FETCH_HOUSEHOLD_SUITABILITY,
  FETCH_MODEL_PORTFOLIOS,
  FETCH_SUB_ACCOUNT,
  FETCH_THEMES,
  FETCH_USER_SUITABILITY,
  modelPortfoliosFromFetchResult,
  type PortfolioDataTransformConfig,
  resolveAssignPortfolioHouseholdOwnership,
  SUGGEST_PORTFOLIO,
  type SubAccountPortfolioData,
  selectSuitabilitySourceData,
  shouldFetchModelPortfoliosForTheme,
  shouldSkipFetchUserSuitability,
  suitabilityScore,
  transformGoalPortfolioData,
  transformMultiSubAccountPortfolioData,
  transformSingleSubAccountPortfolioData,
  UPDATE_GOAL,
  UPDATE_SUB_ACCOUNT,
} from './utils';

const MOCK_CUSTODIAN_SUITABILITY_SCORE = 10;

export const AssignPortfolioToSubAccount = ({
  options,
  stepLoading,
  workflowCompletion,
  objectId,
  userId,
  objectType = WorkflowObjectTypes.User,
  onNext,
}: {
  options: any;
  objectId: string;
  userId: string;
  objectType?: string;
  onNext: () => void;
  stepLoading: boolean;
  workflowCompletion?: any;
}) => {
  const { activeOrganization } = useContext(UserContext);
  const { workflowData } = useContext(WorkflowContext);
  const { enqueueToast } = useToast();
  const { t } = useTranslation('workflowCompletions');
  const contextAware = useFeatureFlag(FlagsmithFlags.context_aware_workflows);

  // Unified state - always an array (length 1 for single mode, N for multi mode)
  const [subAccountsData, setSubAccountsData] = useState<
    SubAccountPortfolioData[]
  >([]);
  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [lockThemeSelection, setLockThemeSelection] = useState<boolean>(false);
  const [skippingHouseholdPortfolio, setSkippingHouseholdPortfolio] =
    useState(false);
  const householdPortfolioSkipStarted = useRef(false);
  const modelPortfolioLoadsInFlight = useRef(new Set<string>());

  // Options
  const setPortfolioOnGoal =
    options.setPortfolioOnGoal && activeOrganization?.allowPortfolioPerGoal;
  const isCreateSubAccountForAccountHardcodedWorkflow =
    workflowCompletion.id === 'create-sub-account-workflow' &&
    workflowData.currentGoalId;
  const manuallyAssignModelPortfolios = options?.manuallyAssignModelPortfolios;
  const enableMultiSubAccountMode = options?.enableMultiSubAccountMode || false;
  const useExternalSuitabilityScore =
    options.useExternalSuitabilityScore && CONFIG.app.env !== 'production';

  const householdClientGroupId =
    objectType === PageObjectType.HOUSEHOLD
      ? objectId
      : workflowData.currentHouseholdClientGroupId;

  // Computed values
  const currentSubAccount = subAccountsData[activeIndex];
  const showTabs = enableMultiSubAccountMode && subAccountsData.length > 1;

  // Helper functions for state management
  const updateSubAccountData = useCallback(
    (index: number, updates: Partial<SubAccountPortfolioData>) => {
      setSubAccountsData((prev) =>
        prev.map((data, i) => (i === index ? { ...data, ...updates } : data)),
      );
    },
    [],
  );

  const updateCurrentSubAccount = useCallback(
    (updates: Partial<SubAccountPortfolioData>) => {
      updateSubAccountData(activeIndex, updates);
    },
    [activeIndex, updateSubAccountData],
  );

  const { loading: householdLoading, data: householdData } = useQuery(
    FETCH_HOUSEHOLD_SUITABILITY,
    {
      variables: { clientGroupId: householdClientGroupId ?? '' },
      skip: !householdClientGroupId,
    },
  );

  const householdOwnsModelFromQuery = useMemo(
    () =>
      householdOwnsPortfolio(
        householdData?.fetchClientGroup?.clientGroup?.financialProduct?.id,
      ),
    [householdData],
  );

  const shouldSkipPortfolioAssignmentQueries =
    Boolean(householdClientGroupId) &&
    (householdLoading || householdOwnsModelFromQuery);

  const { loading: userLoading, data: userSuitabilityData } = useQuery(
    FETCH_USER_SUITABILITY,
    {
      variables: { userId },
      skip: shouldSkipFetchUserSuitability(
        objectType,
        shouldSkipPortfolioAssignmentQueries,
      ),
    },
  );

  const suitabilitySourceData = selectSuitabilitySourceData(
    objectType,
    householdData,
    userSuitabilityData,
  );

  const usesSingleSubAccountQuery =
    !setPortfolioOnGoal &&
    !isCreateSubAccountForAccountHardcodedWorkflow &&
    !enableMultiSubAccountMode &&
    !shouldSkipPortfolioAssignmentQueries;
  // FETCH_SUB_ACCOUNT requires an id; without one the request is rejected and
  // the step is left with nothing to assign.
  const missingSubAccount =
    contextAware &&
    usesSingleSubAccountQuery &&
    !workflowData.currentSubAccountId;
  const missingSubAccountToastShown = useRef(false);

  useEffect(() => {
    if (!missingSubAccount || missingSubAccountToastShown.current) return;
    missingSubAccountToastShown.current = true;
    enqueueToast(
      t('assignPortfolioToSubAccountSubStep.missingSubAccountError'),
      {
        variant: 'error',
      },
    );
  }, [missingSubAccount, enqueueToast, t]);

  // GraphQL queries and mutations
  const { data, loading: fetchSubAccountLoading } = useQuery(
    FETCH_SUB_ACCOUNT,
    {
      variables: { subAccountId: workflowData.currentSubAccountId },
      skip: !usesSingleSubAccountQuery || missingSubAccount,
      fetchPolicy: 'no-cache',
    },
  );

  const [
    fetchSubAccountLazy,
    { data: lazySubAccountData, loading: lazySubAccountLoading },
  ] = useLazyQuery(FETCH_SUB_ACCOUNT, {
    fetchPolicy: 'no-cache',
  });

  const { data: goalData, loading: fetchGoalLoading } = useQuery(
    enableMultiSubAccountMode ? FETCH_GOAL_WITH_SUBACCOUNTS : FETCH_GOAL,
    {
      variables: { goalId: workflowData.currentGoalId },
      skip:
        !workflowData.currentGoalId ||
        shouldSkipPortfolioAssignmentQueries ||
        (enableMultiSubAccountMode
          ? false
          : !setPortfolioOnGoal &&
            !isCreateSubAccountForAccountHardcodedWorkflow),
      fetchPolicy: 'no-cache',
    },
  );

  const { loading: allThemesLoading, data: allThemesData } = useQuery(
    FETCH_THEMES,
    {
      variables: {
        input: buildAssignPortfolioFetchThemesInput(activeOrganization.id),
      },
      skip:
        !manuallyAssignModelPortfolios || shouldSkipPortfolioAssignmentQueries,
    },
  );

  const [suggestPortfolio, { loading: suggestPortfolioLoading }] =
    useLazyQuery(SUGGEST_PORTFOLIO);
  const [fetchModelPortfolios, { loading: modelPortfolioLoading }] =
    useLazyQuery(FETCH_MODEL_PORTFOLIOS);
  const [updateSubAccount, { loading: updateSubAccountLoading }] =
    useMutation(UPDATE_SUB_ACCOUNT);
  const [updateGoal, { loading: updateGoalLoading }] = useMutation(UPDATE_GOAL);

  const fetchedSubAccount =
    data?.fetchSubAccount?.subAccount ??
    lazySubAccountData?.fetchSubAccount?.subAccount;
  const fetchedGoal = goalData?.fetchGoal?.goal;

  const householdOwnsModel = useMemo(
    () =>
      resolveAssignPortfolioHouseholdOwnership({
        householdClientGroupFinancialProductId:
          householdData?.fetchClientGroup?.clientGroup?.financialProduct?.id,
        fetchedSubAccount,
        fetchedGoal,
        enableMultiSubAccountMode,
      }),
    [householdData, fetchedSubAccount, fetchedGoal, enableMultiSubAccountMode],
  );

  const skipHouseholdPortfolioStep = useCallback(async () => {
    if (householdPortfolioSkipStarted.current) {
      return;
    }

    householdPortfolioSkipStarted.current = true;
    setSkippingHouseholdPortfolio(true);
    try {
      if (workflowData.currentSubAccountId) {
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: workflowData.currentSubAccountId,
              skipIPS: true,
            },
          },
        });
      }

      onNext();
    } catch {
      householdPortfolioSkipStarted.current = false;
      enqueueToast(
        t('assignPortfolioToSubAccountSubStep.skipHouseholdPortfolioError'),
        { variant: 'error' },
      );
    } finally {
      setSkippingHouseholdPortfolio(false);
    }
  }, [
    onNext,
    enqueueToast,
    t,
    updateSubAccount,
    workflowData.currentSubAccountId,
  ]);

  // Fetch model portfolios for a specific theme
  const fetchModelPortfoliosForTheme = useCallback(
    async (themeId: string): Promise<any[]> => {
      const result = await fetchModelPortfolios({
        variables: {
          input: {
            filter: {
              organizationId: activeOrganization.id,
              themeId,
              state: 'ACTIVE',
            },
            pagination: { perPage: 100 },
          },
        },
        fetchPolicy: 'no-cache',
      });
      const modelPortfolios = modelPortfoliosFromFetchResult(result);
      if (modelPortfolios === undefined) {
        throw result.error ?? new Error('fetchModelPortfolios failed');
      }
      return modelPortfolios;
    },
    [fetchModelPortfolios, activeOrganization.id],
  );

  // Suggest portfolio for a specific subaccount
  const suggestPortfolioForSubAccount = useCallback(
    async (
      index: number,
      subAccountData: SubAccountPortfolioData,
    ): Promise<void> => {
      if (!subAccountData?.userSuitability || !subAccountData?.themeId) return;

      const result = await suggestPortfolio({
        variables: {
          suitabilityScore: suitabilityScore(
            subAccountData,
            useExternalSuitabilityScore,
            options,
          ),
          organizationId: activeOrganization.id,
          themeId: subAccountData.themeId,
        },
        fetchPolicy: 'network-only',
      });

      if (result.data) {
        const suggestedProduct =
          result.data.suggestFinancialProduct.financialProduct;
        const requestedThemeId = subAccountData.themeId;
        setSubAccountsData((prev) =>
          applySuggestedPortfolio({
            subAccounts: prev,
            index,
            requestedThemeId,
            suggestedProduct,
            manuallyAssignModelPortfolios,
          }),
        );
      }
    },
    [
      useExternalSuitabilityScore,
      suggestPortfolio,
      activeOrganization.id,
      manuallyAssignModelPortfolios,
      options.enableMultiSubAccountMode,
    ],
  );

  // Initialize unified subaccount portfolio data array
  useEffect(() => {
    const userSuitability = determineUserSuitability(
      objectType,
      suitabilitySourceData,
    );

    // Create configuration object for portfolio data transformation
    const config: PortfolioDataTransformConfig = {
      manuallyAssignModelPortfolios,
      userSuitability,
      custodianSuitabilityScore: MOCK_CUSTODIAN_SUITABILITY_SCORE,
      currentAccountId: workflowData.currentAccountId || '',
      allThemesData,
      isCreateSubAccountForAccountHardcodedWorkflow:
        !!isCreateSubAccountForAccountHardcodedWorkflow,
    };

    if (enableMultiSubAccountMode && goalData?.fetchGoal?.goal?.subAccounts) {
      // Multi-subaccount mode: use goal subaccounts
      const portfolioData = transformMultiSubAccountPortfolioData(
        goalData.fetchGoal.goal.subAccounts,
        config,
      );
      setSubAccountsData(portfolioData);
    } else if (!enableMultiSubAccountMode) {
      // Single subaccount mode: use current subaccount or goal
      let portfolioData: SubAccountPortfolioData[] = [];

      if (data?.fetchSubAccount?.subAccount) {
        // Use fetched subaccount
        const { subAccount } = data.fetchSubAccount;
        portfolioData = transformSingleSubAccountPortfolioData(
          subAccount,
          config,
        );
        // Override the ID and name for current subaccount
        portfolioData[0].id = workflowData.currentSubAccountId || '';
        portfolioData[0].name = 'Current SubAccount';
      } else if (goalData?.fetchGoal?.goal) {
        // Try to use goal data first
        const { goal } = goalData.fetchGoal;
        portfolioData = transformGoalPortfolioData(
          goal,
          config,
          workflowData.currentGoalId || '',
        );

        // If transformGoalPortfolioData returns empty (hardcoded workflow with no goal themeId),
        // we need to fetch subAccount data as fallback
        if (
          portfolioData.length === 0 &&
          !lazySubAccountData &&
          !lazySubAccountLoading
        ) {
          fetchSubAccountLazy({
            variables: { subAccountId: workflowData.currentSubAccountId },
          });
          return; // Wait for lazy query to complete
        }

        // Use lazy subAccount data for fallback if available
        if (
          portfolioData.length === 0 &&
          lazySubAccountData?.fetchSubAccount?.subAccount
        ) {
          const { subAccount } = lazySubAccountData.fetchSubAccount;
          portfolioData = transformSingleSubAccountPortfolioData(
            subAccount,
            config,
          );
          // Override the ID and name for current subaccount
          portfolioData[0].id = workflowData.currentSubAccountId || '';
          portfolioData[0].name = 'Current SubAccount';
        }
      }

      if (portfolioData.length > 0) {
        setSubAccountsData(portfolioData);
      }
    }
  }, [
    enableMultiSubAccountMode,
    goalData,
    data,
    lazySubAccountData,
    lazySubAccountLoading,
    suitabilitySourceData,
    allThemesData,
    manuallyAssignModelPortfolios,
    objectType,
    workflowData.currentSubAccountId,
    workflowData.currentGoalId,
    workflowData.currentAccountId,
    isCreateSubAccountForAccountHardcodedWorkflow,
    fetchSubAccountLazy,
  ]);

  useEffect(() => {
    if (!householdOwnsModel || householdPortfolioSkipStarted.current) {
      return;
    }

    if (householdClientGroupId) {
      if (householdLoading) {
        return;
      }
    } else if (
      (fetchSubAccountLoading || fetchGoalLoading || userLoading) &&
      !fetchedSubAccount &&
      !fetchedGoal
    ) {
      return;
    }

    void skipHouseholdPortfolioStep();
  }, [
    householdOwnsModel,
    householdClientGroupId,
    householdLoading,
    fetchSubAccountLoading,
    fetchGoalLoading,
    userLoading,
    fetchedSubAccount,
    fetchedGoal,
    skipHouseholdPortfolioStep,
  ]);

  // Fetch model portfolios when theme changes for manual assignment
  useEffect(() => {
    const themeId = currentSubAccount?.themeId;
    const themeIsListed = currentSubAccount?.availableThemes?.some(
      (theme) => theme.id === themeId,
    );
    const requestKey = [currentSubAccount?.id, themeId].join(':');
    if (!themeIsListed) {
      if (!themeId) return;
      updateCurrentSubAccount({ ...currentSubAccount, themeId: null });
      return;
    }
    if (
      themeId &&
      shouldFetchModelPortfoliosForTheme({
        manuallyAssignModelPortfolios,
        themeId,
        modelPortfoliosThemeId: currentSubAccount.modelPortfoliosThemeId,
        modelPortfolioLoadFailed: currentSubAccount.modelPortfolioLoadFailed,
        modelPortfolioLoadInFlight:
          modelPortfolioLoadsInFlight.current.has(requestKey),
      })
    ) {
      const index = activeIndex;
      modelPortfolioLoadsInFlight.current.add(requestKey);
      fetchModelPortfoliosForTheme(themeId)
        .then((availableProducts) => {
          setSubAccountsData((prev) => {
            const current = prev[index];
            if (current?.themeId !== themeId) return prev;
            return prev.map((data, i) =>
              i === index
                ? {
                    ...data,
                    availableFinancialProducts: availableProducts,
                    modelPortfoliosThemeId: themeId,
                    modelPortfolioLoadFailed: false,
                  }
                : data,
            );
          });
          // Auto-suggest portfolio after loading model portfolios
          if (
            availableProducts.length > 0 &&
            currentSubAccount.userSuitability
          ) {
            suggestPortfolioForSubAccount(index, {
              ...currentSubAccount,
              availableFinancialProducts: availableProducts,
            });
          }
        })
        .catch(() => {
          setSubAccountsData((prev) => {
            const current = prev[index];
            if (
              current?.themeId !== themeId ||
              current.modelPortfoliosThemeId === themeId
            ) {
              return prev;
            }
            return prev.map((data, i) =>
              i === index
                ? {
                    ...data,
                    modelPortfolioLoadFailed: true,
                    modelPortfolioLoadFailureVersion:
                      data.modelPortfolioLoadFailureVersion + 1,
                  }
                : data,
            );
          });
        })
        .finally(() => {
          modelPortfolioLoadsInFlight.current.delete(requestKey);
        });
    }
  }, [
    manuallyAssignModelPortfolios,
    currentSubAccount?.themeId,
    currentSubAccount?.modelPortfoliosThemeId,
    currentSubAccount?.userSuitability,
    currentSubAccount,
    fetchModelPortfoliosForTheme,
    updateCurrentSubAccount,
    suggestPortfolioForSubAccount,
    activeIndex,
  ]);

  // Auto-suggest portfolios when data is ready
  useEffect(() => {
    if (subAccountsData.length > 0) {
      // Suggest portfolios for all subaccounts that need suggestions
      subAccountsData.forEach((subAccountData, index) => {
        if (
          subAccountData.themeId &&
          subAccountData.userSuitability &&
          !subAccountData.suggestedFinancialProduct
        ) {
          suggestPortfolioForSubAccount(index, subAccountData);
        }
      });
    }
  }, [subAccountsData, suggestPortfolioForSubAccount]);

  // Set theme locking when needed
  useEffect(() => {
    if (
      isCreateSubAccountForAccountHardcodedWorkflow &&
      goalData?.fetchGoal?.goal?.financialProduct?.theme?.id
    ) {
      setLockThemeSelection(true);
    }
  }, [
    isCreateSubAccountForAccountHardcodedWorkflow,
    goalData?.fetchGoal?.goal?.financialProduct?.theme?.id,
  ]);

  // Handle lowest suggestion fallback for manual portfolios when no suitability
  useEffect(() => {
    if (
      currentSubAccount?.availableFinancialProducts?.length &&
      (!currentSubAccount.userSuitability ||
        currentSubAccount.suggestionMissed) &&
      !currentSubAccount.financialProduct
    ) {
      const lowestSuggestion =
        currentSubAccount.availableFinancialProducts.reduce(
          (lowest: any, modelPortfolio: any) =>
            modelPortfolio.riskReturnLevel < lowest.riskReturnLevel
              ? modelPortfolio
              : lowest,
          currentSubAccount.availableFinancialProducts[0],
        );

      updateCurrentSubAccount({
        financialProduct: lowestSuggestion,
        suggestionMissed: false,
      });
    }
  }, [
    currentSubAccount?.availableFinancialProducts,
    currentSubAccount?.userSuitability,
    currentSubAccount?.financialProduct,
    currentSubAccount?.suggestionMissed,
    updateCurrentSubAccount,
  ]);

  // Handle theme changes
  const handleThemeChange = useCallback(
    (newThemeId: string) => {
      updateCurrentSubAccount({
        themeId: newThemeId,
        financialProduct: null,
        availableFinancialProducts: [],
        modelPortfoliosThemeId: null,
        modelPortfolioLoadFailed: false,
        suggestionMissed: false,
      });

      if (!manuallyAssignModelPortfolios && currentSubAccount) {
        suggestPortfolioForSubAccount(activeIndex, {
          ...currentSubAccount,
          themeId: newThemeId,
          financialProduct: null,
        });
      }
    },
    [
      updateCurrentSubAccount,
      manuallyAssignModelPortfolios,
      suggestPortfolioForSubAccount,
      activeIndex,
      currentSubAccount,
    ],
  );

  // Handle financial product selection (for manual mode)
  const handleFinancialProductChange = useCallback(
    (productId: string) => {
      const selectedProduct =
        currentSubAccount?.availableFinancialProducts?.find(
          (fp: any) => fp.id === productId,
        );
      if (selectedProduct) {
        updateCurrentSubAccount({ financialProduct: selectedProduct });
      }
    },
    [currentSubAccount?.availableFinancialProducts, updateCurrentSubAccount],
  );

  // Computed visual data
  const visualData = useMemo(() => {
    if (!currentSubAccount) {
      return {
        id: null,
        themeId: null,
        financialProduct: null,
        availableThemes: [],
        availableFinancialProducts: [],
        modelPortfoliosThemeId: null,
        modelPortfolioLoadFailed: false,
        modelPortfolioLoadFailureVersion: 0,
        userSuitability: null,
      };
    }

    return {
      id: currentSubAccount.id,
      themeId: currentSubAccount.themeId,
      financialProduct: currentSubAccount.financialProduct,
      availableThemes: currentSubAccount.availableThemes,
      availableFinancialProducts: currentSubAccount.availableFinancialProducts,
      modelPortfoliosThemeId: currentSubAccount.modelPortfoliosThemeId,
      modelPortfolioLoadFailed: currentSubAccount.modelPortfolioLoadFailed,
      modelPortfolioLoadFailureVersion:
        currentSubAccount.modelPortfolioLoadFailureVersion,
      userSuitability: currentSubAccount.userSuitability,
    };
  }, [currentSubAccount]);

  // Tab data for visual component
  const tabsData = useMemo(() => {
    if (!showTabs) return [];

    return subAccountsData.map((subAccount) => ({
      label: subAccount.name
        ? getLanguageDefaults(subAccount.name)
        : getTranslations({
            fileName: 'client',
            translationPath: ['subAccountWithSuffix'],
            variables: { suffix: subAccount.id.slice(-4) },
          }),
      icon: 'account_balance_wallet',
      widgets: [],
    }));
  }, [showTabs, subAccountsData]);

  // Save portfolios
  const setPortfolio = async () => {
    if (householdOwnsModel) {
      await skipHouseholdPortfolioStep();
      return;
    }

    if (missingSubAccount) return;

    if (!currentSubAccount?.themeId || !currentSubAccount?.financialProduct)
      return;

    if (setPortfolioOnGoal) {
      // Update goal with portfolio
      if (workflowData.currentGoalId) {
        await updateGoal({
          variables: {
            input: {
              goalId: workflowData.currentGoalId,
              financialProductId: manuallyAssignModelPortfolios
                ? currentSubAccount.financialProduct.id
                : currentSubAccount.suggestedFinancialProduct?.id,
            },
          },
        });
      }

      // Update all subaccounts to skip IPS
      if (workflowData.currentSubAccountId) {
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: workflowData.currentSubAccountId,
              skipIPS: true,
            },
          },
        });
      }
    } else {
      // Update individual subaccounts with their portfolios
      const shouldSkipIPS = false; // Normal subAccount updates don't skip IPS

      if (enableMultiSubAccountMode) {
        // Multi-subaccount mode: update all subaccounts
        const updatePromises = subAccountsData.map((subAccountData) => {
          if (!subAccountData.themeId) return Promise.resolve();

          const financialProduct = manuallyAssignModelPortfolios
            ? subAccountData.financialProduct
            : subAccountData.suggestedFinancialProduct;

          if (!financialProduct) return Promise.resolve();

          return updateSubAccount({
            variables: {
              input: {
                subAccountId: subAccountData.id,
                themeId: subAccountData.themeId,
                financialProductId: shouldSkipIPS
                  ? undefined
                  : financialProduct.id,
                skipIPS: shouldSkipIPS || false,
              },
            },
          });
        });

        await Promise.all(updatePromises.filter(Boolean));
      } else if (currentSubAccount.isFromGoal) {
        // Hardcoded workflow with goal themeId: just skip IPS on subaccount
        if (workflowData.currentSubAccountId) {
          await updateSubAccount({
            variables: {
              input: {
                subAccountId: workflowData.currentSubAccountId,
                skipIPS: true,
              },
            },
          });
        }
      } else {
        // Regular subaccount update
        await updateSubAccount({
          variables: {
            input: {
              subAccountId: currentSubAccount.id,
              themeId: currentSubAccount.themeId,
              financialProductId: shouldSkipIPS
                ? undefined
                : manuallyAssignModelPortfolios
                  ? currentSubAccount.financialProduct.id
                  : currentSubAccount.suggestedFinancialProduct?.id,
              skipIPS: shouldSkipIPS || false,
            },
          },
        });
      }
    }

    await onNext();
  };

  const isLoading =
    skippingHouseholdPortfolio ||
    householdLoading ||
    fetchSubAccountLoading ||
    lazySubAccountLoading ||
    stepLoading ||
    fetchGoalLoading ||
    modelPortfolioLoading ||
    suggestPortfolioLoading ||
    allThemesLoading ||
    userLoading ||
    updateSubAccountLoading ||
    updateGoalLoading;

  return (
    <AssignPortfolioToSubAccountVisual
      options={options}
      objectData={visualData}
      suggestedFinancialProduct={currentSubAccount?.suggestedFinancialProduct}
      updateObject={{
        handleThemeChange,
        handleFinancialProductChange,
      }}
      continueFunc={setPortfolio}
      loading={isLoading}
      workflowCompletion={workflowCompletion}
      lockThemeSelection={lockThemeSelection}
      manuallyAssignModelPortfolios={manuallyAssignModelPortfolios}
      showTabs={showTabs}
      tabsData={tabsData}
      activeSubAccountIndex={activeIndex}
      setActiveSubAccountIndex={setActiveIndex}
      householdOwnsModel={householdOwnsModel}
      errorMessage={
        missingSubAccount
          ? t('assignPortfolioToSubAccountSubStep.missingSubAccountError')
          : undefined
      }
    />
  );
};

export default AssignPortfolioToSubAccount;
